import React, { useRef, useState } from "react";
import { Upload, X, Loader2, Music } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage, formatApiErrorDetail } from "@/lib/api";

// Audio file uploader — accepts MP3 / M4A / WAV / OGG up to 15MB.
// Stores the file via /admin/audio-uploads which lives in MongoDB (survives deploys).
export default function AudioUploader({
  value,
  onChange,
  label = "Audio file (MP3 from Suno)",
  testIdPrefix = "audio-uploader",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/")) {
      toast.error("Please choose an audio file (MP3, M4A, WAV, or OGG).");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Audio file must be 15MB or smaller.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/audio-uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.url);
      toast.success("Audio uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => onChange?.("");

  return (
    <div data-testid={`${testIdPrefix}-root`}>
      <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
        {label}
      </label>

      {value ? (
        <div className="border border-rule p-4 bg-paper-2/40 flex items-center gap-3">
          <Music size={18} className="text-terracotta shrink-0" />
          <audio
            controls
            src={resolveImage(value)}
            data-testid={`${testIdPrefix}-preview`}
            className="flex-1 min-w-0"
          />
          <button
            type="button"
            onClick={clear}
            data-testid={`${testIdPrefix}-clear`}
            className="border border-ink text-ink hover:bg-ink hover:text-paper p-1.5"
            title="Remove audio"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div
          className="border border-dashed border-rule bg-paper-2/40 p-6 text-center"
          data-testid={`${testIdPrefix}-empty`}
        >
          <p className="font-serif-display text-lg italic text-ink-soft mb-2">
            No audio yet
          </p>
          <p className="text-xs text-ink-soft">
            Download your song from Suno (MP3) and upload it here. Max 15MB.
          </p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="audio/mpeg,audio/mp3,audio/mp4,audio/m4a,audio/x-m4a,audio/wav,audio/wave,audio/x-wav,audio/ogg"
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      <div className="flex items-center gap-3 mt-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testIdPrefix}-upload-btn`}
          className="inline-flex items-center gap-2 px-4 py-2 border border-ink text-ink text-xs tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 className="animate-spin" size={14} /> Uploading…
            </>
          ) : (
            <>
              <Upload size={14} /> {value ? "Replace audio" : "Upload audio"}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
